import React, { useState, useEffect } from 'react';
import { Badge } from 'react-bootstrap';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import '@fortawesome/fontawesome-free/css/all.min.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import './empStyle.css';
import config from '../../config.json';

const apiUrl = config.apiUrl;

const getStatusBadge = (status) => {
  let variant;
  switch (status) {
    case 'Inactive':
      variant = 'danger';
      break;
    case 'Active':
      variant = 'primary';
      break;
    default:
      variant = 'secondary';
  }
  return <Badge bg={variant}>{status}</Badge>;
};

const EmpList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [employeeData, setEmployeeData] = useState([]);
  const [dropdownStates, setDropdownStates] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roles = JSON.parse(localStorage.getItem('Roles'));

  const userAccess = {
    hasView: roles?.includes('View') || false,
    hasAdmin: roles?.includes('Admin') || false,
    hasApprover: roles?.includes('Approver') || false,
    hasStandard: roles?.includes('Standard') || false,
  };

  const { hasView, hasAdmin, hasStandard, hasApprover } = userAccess;


  const toggleDropdown = (itemId) => {
    setDropdownStates((prevState) => ({
      ...prevState,
      [itemId]: !prevState[itemId],
    }));
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token === null) {
      // Token is null, reload the page
      window.location.reload();
    } else {
      // Token is present, fetch employee data
      const fetchEmployeeData = async () => {
        try {
          const response = await fetch(`${apiUrl}/Employee?searchTerm=${searchInput}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
          });

          const data = await response.json();
          if (!response.ok) {
            if (response.status === 401) {
              // Redirect to login page if unauthorized
              window.location.href = `${config.identityUrl}/Identity/Account/Login`;
            } else {
              setErrorMessage('Failed to fetch data');
              setSuccessMessage('');
              return;
            }

          }

          setEmployeeData(data.Employee);
        } catch (error) {
          setErrorMessage('Error fetching data');
          setSuccessMessage('');
        }
        setIsLoading(false);
      };

      fetchEmployeeData();
    }
  }, [searchInput]);

  const handleStatus = async (employeeID) => {
    const token = localStorage.getItem('token');
    if (window.confirm('Do you want to change status?') === true) {
      try {
        const response = await fetch(`${apiUrl}/Employee/ToggleStatus/${employeeID}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        if (!response.ok) {
          if (response.status === 401) {
            // Redirect to login page if unauthorized
            window.location.href = `${config.identityUrl}/Identity/Account/Login`;
          } else {
            setErrorMessage('Error to fetch data');
            setSuccessMessage('');
            return;
          }
        }

        setSuccessMessage(data.Message);
        setErrorMessage('');

        navigate('/employee-list');
      } catch (error) {
        setSuccessMessage('Error changing status');
        setErrorMessage('');
      }
    }
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = employeeData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(employeeData.length / itemsPerPage);

  const pageRange = 5;
  const startPage = Math.max(1, currentPage - Math.floor(pageRange / 2));
  const endPage = Math.min(totalPages, startPage + pageRange - 1);

  return (
    <div>
      {(userAccess.hasAdmin || userAccess.hasStandard) ? (
        <>
          <h2>Employee</h2>
          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
          {successMessage && <div className="alert alert-success">{successMessage}</div>}
          {userAccess.hasAdmin && (
            <Link to="/employee-create" className="btn btn-primary float-end mb-2">
              Add Employee
            </Link>
          )}
          <div className="custom-width mb-3">
            <input
              type="text"
              placeholder="Search"
              className="form-control"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
          <table className="custom-table">
            <thead>
              <tr>
                <th></th>
                <th>ID Number</th>
                <th>Last Name</th>
                <th>First Name</th>
                <th>Middle Initial</th>
                <th>Position</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="9">
                    <div class="d-flex align-items-center">
                      <div class="spinner-border me-2" aria-hidden="true"></div>
                      <span role="status"> Loading...</span>
                    </div>
                  </td>
                </tr>
              ) : (
              currentItems && currentItems.length > 0 ? (
                currentItems.map((item) => (
                  <tr key={item.ID}>
                    <td className="text-sm-center remove-space">
                      <div className="dropdown">
                        <button
                          className="btn"
                          type="button"
                          id={`dropdownMenuButton${item.ID}`}
                          data-bs-toggle="dropdown"
                          data-bs-auto-close="outside"
                          aria-haspopup="true"
                          aria-expanded={dropdownStates[item.ID]}
                          onClick={() => toggleDropdown(item.ID)}
                        >
                          <i className="fas fa-ellipsis"></i>
                        </button>
                        <div
                          className={`dropdown-menu ${dropdownStates[item.ID] ? 'show' : ''}`}
                          aria-labelledby={`dropdownMenuButton${item.ID}`}
                        >
                          {userAccess.hasAdmin && (
                            <Link
                              to={`/employee-update/${item.ID}`}
                              className={`dropdown-item ${item.Status === 'Inactive' ? 'disabled' : ''}`}
                            >
                              Update
                            </Link>
                          )}
                          {(userAccess.hasView || userAccess.hasAdmin) && (
                            <Link to={`/employee-view/${item.ID}`} className="dropdown-item">
                              View
                            </Link>
                          )}
                          {userAccess.hasAdmin && (
                            <Link to={`/employee-delete/${item.ID}`} className="dropdown-item">
                              Delete
                            </Link>
                          )}
                          {userAccess.hasAdmin && (
                            <button
                              onClick={() => handleStatus(item.ID)}
                              className={`dropdown-item ${item.Status === 'Activate' ? 'text-success' : 'text-danger'
                                }`}
                            >
                              {item.Status !== 'Active' ? 'Activate' : 'Deactivate'}
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{item.IdNumber}</td>
                    <td>{item.LastName}</td>
                    <td>{item.FirstName}</td>
                    <td>{item.MiddleInitial}</td>
                    <td>{item.Position}</td>
                    <td>{getStatusBadge(item.Status)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7">No Record</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="Page navigation">
            <ul className="pagination mt-3">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <Link
                  to={`/employee-list?page=${currentPage - 1}`}
                  className="page-link"
                  onClick={() => handlePageChange(currentPage - 1)}
                  aria-label="Previous"
                >
                  <span aria-hidden="true">&laquo;</span>
                </Link>
              </li>
              {Array.from({ length: endPage - startPage + 1 }, (_, index) => (
                <li
                  key={index}
                  className={`page-item ${currentPage === startPage + index ? 'active' : ''}`}
                >
                  <Link
                    to={`/employee-list?page=${startPage + index}`}
                    className="page-link"
                    onClick={() => handlePageChange(startPage + index)}
                  >
                    {startPage + index}
                  </Link>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <Link
                  to={`/employee-list?page=${currentPage + 1}`}
                  className="page-link"
                  onClick={() => handlePageChange(currentPage + 1)}
                  aria-label="Next"
                >
                  <span aria-hidden="true">&raquo;</span>
                </Link>
              </li>
            </ul>
          </nav>
        </>
      ) : (
        <div>
          <h3>No access!</h3>
          <button className="btn btn-primary" onClick={() => navigate(-1)}>
            Go Back
          </button>
        </div>
      )}
    </div>
  );
};

export default EmpList;